import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import AiMessageBubble from "./components/AiMessageBubble";
import PatientGenderInput from "./components/PatientGenderInput";
import PatientAgeInput from "./components/PatientAgeInput";
import ExamplePrompts from "./components/ExamplePrompts";
import BottomSheetWrapper, {
  BottomSheetWrapperRef,
} from "../../commonComponents/BottomSheetWrapper";
import DDxInputField from "./components/DDxInputField";
import DiagnosisView from "./components/DiagnosisView";
import HistoryDrawer from "./components/HistoryDrawer";
import Button from "../home/components/Button";

import { ReactComponent as DDxHistory } from "../../assets/images/ddx-history.svg";
import { ReactComponent as DDxNewChat } from "../../assets/images/ddx-new-chat.svg";
import { ReactComponent as ArrowLeft } from "../../assets/images/arrow-left.svg";
import BGImage from "../../assets/images/ddx-generate-bg.png";
import { ReactComponent as Close } from "../../assets/images/close.svg";
import { getDecodedToken, getLocalStorage } from "../../utils/localStorage";
import { getB2cIdWithFallbacks } from "../../utils/b2cHelper";
import {
  generateDDx,
  getCredits,
  getDDxHistory,
  getDDxHistoryById,
  transcribeInput,
  transcribeInputViaGemini,
} from "./service";
import { AnimatePresence, motion, Variants } from "framer-motion";
import PatientTextInput from "./components/PatientTextInput";
import { notification, Spin } from "antd";
import useStore from "../../store/useStore";
import ShimmerSkeleton from "./components/SkeletonLoader";
import coin from "../../assets/images/tatvacoin.png";
import { FROM_TATAV_PRACTICE } from "../../utils/constants";
import { navigateToTatvaPractice } from "../../utils/helper";
import { getAppType } from "../../utils/appConfig";
import { trackZymedEvent, useZymedOpenedEvent, ZYMED_EVENTS } from "../../utils/zymedAnalytics";

type HeaderButton = {
  icon?: React.ReactNode;
  onClick: () => void;
};

type HeaderProps = {
  title: string;
  onPressBack: () => void;
  rightButtons?: HeaderButton[];
  credits?: number;
};

const Header: React.FC<HeaderProps> = ({
  title,
  rightButtons = [],
  onPressBack,
  credits,
}) => {
  const appType = useMemo(() => getAppType(), []);

  return (
    <div className="sticky top-0 px-4 py-3 bg-white flex flex-row items-center justify-between z-20">
      <div className="flex flex-row items-center gap-1.5 bg-white">
        <button onClick={onPressBack}>
          <ArrowLeft />
        </button>
        <h3 className="text-gray-800 font-semibold">{title}</h3>
      </div>
      <div className="flex flex-row items-center gap-6 bg-white">
        {appType !== "zymedelite" &&
          <div className="w-full h-7 bg-orange-50 rounded-3xl border border-amber-500 border-solid backdrop-blur-[20px] items-center flex px-2 gap-1.5">
            <img
              loading="lazy"
              src={coin}
              alt="Coin"
              className="object-contain shrink-0 self-stretch my-auto aspect-[0.94] w-5"
            />
            <p className="font-semibold text-gray-800 text-[14px] mt-0.5 items-center">
              {String(credits).padStart(2, "0")}
            </p>
          </div>
        }
        {rightButtons.map((button, index) => (
          <button key={index.toString()} onClick={button.onClick}>
            {button.icon}
          </button>
        ))}
      </div>
    </div>
  );
};

/**
 * ZymDdx_Generated — the P0 funnel goal. `results` is the ranked differential list the
 * DiagnosisView renders; rank 1 (or the first "most likely") is the primary diagnosis.
 * `/ddx/generate` returns no record id, so case_id is read off the newest history entry,
 * which the backend writes as part of the same generate call.
 */
const trackDdxGenerated = async (data: any): Promise<string | undefined> => {
  const results: any[] = Array.isArray(data?.results) ? data.results : [];
  let case_id = data?._id ?? data?.id ?? data?.historyId ?? data?.caseId;
  if (!case_id) {
    try {
      const history = await getDDxHistory({ page: 1, pageSize: 1 });
      case_id = history?.data?.history?.[0]?._id;
    } catch {
      // Best effort — the event still fires without case_id.
    }
  }
  const byRank = [...results].sort(
    (a, b) => (a?.rank ?? Number.MAX_SAFE_INTEGER) - (b?.rank ?? Number.MAX_SAFE_INTEGER)
  );
  const primary =
    byRank.find((r) => r?.likelihood === "most likely") ?? byRank[0];
  const names = byRank
    .map((r) => r?.differentialDiagnosisName)
    .filter((n): n is string => !!n);
  trackZymedEvent(ZYMED_EVENTS.DDX_GENERATED, {
    case_id,
    primary_diagnosis: primary?.differentialDiagnosisName,
    alternative_diagnosis: names.filter((n) => n !== primary?.differentialDiagnosisName),
    diagnosis_count: results.length,
  });
  return case_id;
};

const GenerateDDxPage = () => {
  const newChatConfirmationRef = useRef<BottomSheetWrapperRef>(null);
  const creditExpiredRef = useRef<BottomSheetWrapperRef>(null);
  // The case the chat is currently on, so the exit / new-chat events can name it. Null
  // until a DDx has actually been generated — the taxonomy marks it nullable for that.
  const lastCaseId = useRef<string | undefined>(undefined);

  const navigate = useNavigate();
  const location = useLocation();
  const urlSearchString = window.location.search;
  const params = new URLSearchParams(urlSearchString);
  const historyId: string | null = params.get("historyId");
  useZymedOpenedEvent(ZYMED_EVENTS.DDX_OPENED, {
    skip: !!(location.state as any)?.fromWelcome,
  });

  const { ddxHistoryCount } = useStore();

  const [isHistoryDrawerOpen, setIsHistoryDrawerOpen] =
    useState<boolean>(false);
  const [inputMode, setInputMode] = useState<
    "audio" | "text" | "converting" | null
  >(null);

  const tokenData: any = getDecodedToken();
  const doctorDetails = tokenData?.user || {};

  const itemVariants: Variants = {
    hidden: { opacity: 0, y: "100%" },
    show: { opacity: 1, y: 0, transition: { duration: 1, ease: "easeOut" } },
  };

  const [loadingHistory, setLoadingHistory] = useState<boolean>(false);

  const [caseId, setCaseId] = useState<string | undefined>(undefined);
  const [gender, setGender] = useState<"M" | "F" | null>(null);
  const [age, setAge] = useState<number | null>(null);
  const [symptomText, setSymptomText] = useState<string>("");

  const [showAgeInput, setShowAgeInput] = useState<boolean>(false);
  const [showSyptomInput, setShowSyptomInput] = useState<boolean>(false);

  const [isEditing, setIsEditing] = useState({
    gender: false,
    age: false,
    symptom: false,
  });

  const [isSendPressed, setIsSendPressed] = useState<boolean>(false);
  const [sending, setSending] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);
  const [sendError, setSendError] = useState<string | null>(null);

  const [generatedDDx, setGeneratedDDx] = useState<any>(null);
  const [credits, setCredits] = useState<number>(0);

  const canUserGenerateDDx = async (): Promise<boolean> => {
    const b2c_id = await getB2cIdWithFallbacks();
    const res = await getCredits(b2c_id);
    if (res?.status === 200) {
      if (res?.data?.body?.ddx_credit > 0) {
        setCredits(res?.data?.body?.ddx_credit);
        return true;
      } else {
        setCredits(0);
        return false;
      }
    } else {
      setCredits(0);
      return false;
    }
  };

  /** The pickers write straight to state; the taxonomy wants the choice itself. */
  const selectGender = (value: "M" | "F" | null) => {
    if (value) {
      trackZymedEvent(ZYMED_EVENTS.DDX_GENDER_SELECTED, {
        gender: value === "M" ? "Male" : "Female",
      });
    }
    setGender(value);
  };

  const selectAge = (value: number | null) => {
    if (value !== null) trackZymedEvent(ZYMED_EVENTS.DDX_AGE_SELECTED, { age: value });
    setAge(value);
  };

  const onEditPress = (field: "gender" | "age" | "symptom") => {
    trackZymedEvent(ZYMED_EVENTS.DDX_EDIT_PROMPT, {
      // The taxonomy calls the third field "symptoms".
      field_edited: field === "symptom" ? "symptoms" : field,
    });
    setIsEditing({
      gender: field === "gender",
      age: field === "age",
      symptom: field === "symptom",
    });
    if (field === "symptom") {
      setIsSendPressed(false);
      setInputMode("text");
      setSendError(null);
    }
  };

  const cancelEditMode = () => {
    setIsSendPressed(true);
    setIsEditing((ie) => ({ ...ie, symptom: false }));
  };

  const onPressBack = () => {
    const fromPractice = getLocalStorage(FROM_TATAV_PRACTICE);
    if (fromPractice && fromPractice === "true") {
      navigateToTatvaPractice()
    } else {
      navigate("/")
    }
  };

  const onClickPrompt = (prompt: string) => {
    setSymptomText(prompt);
    onPressSend(prompt);
  };

  const onPressCancelNewChat = () => {
    trackZymedEvent(ZYMED_EVENTS.DDX_NEW_CHAT_CANCELLED, { case_id: lastCaseId.current });
    newChatConfirmationRef.current?.hide();
  };

  const onPressStartNewChat = () => {
    trackZymedEvent(ZYMED_EVENTS.DDX_NEW_CHAT_CONFIRMED, { case_id: lastCaseId.current });
    setAge(null);
    setGender(null);
    setSymptomText("");
    setIsSendPressed(false);
    setSuccess(false);
    setSendError(null);
    setShowAgeInput(false);
    setShowSyptomInput(false);
    newChatConfirmationRef.current?.hide();
  };

  const onPressHistoryItem = (historyId: string, pastCaseDate?: string) => {
    trackZymedEvent(ZYMED_EVENTS.DDX_HISTORY_ITEM_CLICKED, {
      case_id: historyId,
      past_case_date: pastCaseDate,
    });
    window.location.replace(`/generate-ddx?historyId=${historyId}`);
  };

  const onPressSend = async (
    prompt: string | null = null,
    patientAge?: number,
    patientGender?: "M" | "F" | null
  ) => {
    if (
      !(patientAge ?? age) ||
      !(patientGender ?? gender) ||
      !(prompt?.trim() ?? symptomText?.trim())
    )
      return;

    if (!(await canUserGenerateDDx())) {
      toggleCreditExpiredBottomSheet("open");
      return;
    }

    setIsSendPressed(true);
    setSending(true);
    setSuccess(false);
    setSendError(null);
    setIsEditing({ age: false, gender: false, symptom: false });

    // case_id is minted server-side and only comes back with the generated DDx, so it
    // rides on ZymDdx_Generated / ZymDdx_GenerationFailed rather than delaying this one.
    const submittedText = (prompt?.trim() ?? symptomText?.trim()) || "";
    trackZymedEvent(ZYMED_EVENTS.DDX_SYMPTOM_SUBMITTED, {
      input_mode: inputMode === "audio" || inputMode === "converting" ? "voice" : "text",
      word_count: submittedText.split(/\s+/).filter(Boolean).length,
      symptoms_text_entered: submittedText,
    });

    // Rebuilt per attempt rather than shared: the fallback below issues a second request,
    // and a FormData that has already been handed to a request should not be reused.
    const buildTranscriptionForm = () => {
      const formData = new FormData();
      if (!!(prompt ?? symptomText)) {
        formData.append("text", prompt?.trim() ?? symptomText?.trim());
      }
      if (!!doctorDetails) {
        formData.append("doctor_specialty", doctorDetails?.specialityTxt);
      }
      return formData;
    };

    // The transcription endpoints answer HTTP 200 with `success: true` even when they
    // fail to extract anything: the failure is reported *inside* `rx`, as
    // `{ error: true, message }`. So the HTTP status alone is not enough to trust the
    // body. Without this check the unusable `rx` was spread into the payload,
    // `/ddx/generate` rejected it with `body must have required property 'symptoms'`,
    // and the global axios interceptor dumped that raw schema error on the user's screen.
    //
    // `serviceFailed` separates "the model was unreachable" from "the model ran and found
    // no symptoms", because only the latter is worth asking the user to rephrase.
    const attemptTranscription = async (
      call: (body: any) => Promise<any>
    ): Promise<{ rx: any; serviceFailed: boolean }> => {
      const res = await call(buildTranscriptionForm());
      const rx = res?.data?.rx;
      if (res?.status !== 200 || rx?.error) {
        return { rx: null, serviceFailed: true };
      }
      const hasSymptoms = Array.isArray(rx?.symptoms) && rx.symptoms.length > 0;
      return { rx: hasSymptoms ? rx : null, serviceFailed: false };
    };

    let { rx, serviceFailed } = await attemptTranscription(transcribeInput);

    if (!rx) {
      // /transcribe is backed by OpenAI, and when that upstream is unreachable it returns
      // `LLM error: Connection error.` for every request, taking the whole feature down.
      // /gemini-transcribe is the same service with a different model and an identical
      // `rx` contract. Keeping /transcribe primary means this path stops being used on
      // its own once OpenAI recovers — no redeploy needed to go back.
      ({ rx, serviceFailed } = await attemptTranscription(
        transcribeInputViaGemini
      ));
    }

    if (!rx) {
      trackZymedEvent(ZYMED_EVENTS.DDX_GENERATION_FAILED, {
        error_reason: serviceFailed ? "model_error" : "insufficient_input",
      });
      setGeneratedDDx(null);
      setSuccess(false);
      setSendError(
        serviceFailed
          ? "We couldn't process this right now. Please try again in a moment."
          : "We couldn't pick out any symptoms from that. Please add a little more detail — the symptom, how long it has been going on, and any relevant history."
      );
      setSending(false);
      return;
    }

    // `error`/`message` are transcription-status fields, not clinical data. They must not
    // reach /ddx/generate, whose request schema rejects unknown properties.
    const { error: _rxError, message: _rxMessage, ...rxFields } = rx;

    const payload = {
      transcription: prompt?.trim() ?? symptomText?.trim(),
      doctorSpeciality: doctorDetails?.specialityTxt,
      patientInformation: {
        age: (patientAge ?? age)?.toString(),
        // Must mirror the `patientAge ?? age` fallback above: when the gender picker
        // calls onPressSend it passes the new value as an argument because the `gender`
        // state has not committed yet, so reading state here sent the previous value.
        gender: (patientGender ?? gender) === "M" ? "Male" : "Female",
      },
      ...rxFields,
    };
    payload.symptoms?.forEach((obj: any) => {
      obj.since = obj.duration;
      delete obj.duration;
    });
    const ddx_res = await generateDDx(payload);
    if (ddx_res?.status === 200 || ddx_res?.status === 201) {
      setGeneratedDDx(ddx_res?.data?.results);
      setSuccess(true);
      trackDdxGenerated(ddx_res?.data).then((caseId) => {
        lastCaseId.current = caseId;
        setCaseId(caseId);
      });
    } else {
      trackZymedEvent(ZYMED_EVENTS.DDX_GENERATION_FAILED, {
        error_reason: ddx_res?.status === 408 ? "timeout" : "model_error",
      });
      setGeneratedDDx(null);
      setSuccess(false);
      setSendError(
        "We couldn't generate the diagnosis right now. Please try again in a moment."
      );
    }
    canUserGenerateDDx();
    setSending(false);
  };

  const fetchHistoryById = async () => {
    if (!historyId) return;
    setLoadingHistory(true);
    const history_res = await getDDxHistoryById(historyId);
    if (history_res?.status === 200) {
      setAge(
        parseInt(history_res?.data?.result?.request?.patientInformation?.age)
      );
      setGender(
        history_res?.data?.result?.request?.patientInformation?.gender ===
          "Female"
          ? "F"
          : "M"
      );
      setSymptomText(history_res?.data?.result?.docstaReqTranscription);
      setGeneratedDDx(history_res?.data?.result?.results);
      // Restoring a past case has to re-establish the case the chat is on: the thumbs
      // reaction and the new-chat events read it, and without this they would report a
      // reopened case as having none. setAge / setGender are deliberately the plain
      // setters here — replaying a stored case is not the user picking those values.
      setCaseId(historyId);
      lastCaseId.current = historyId;
      setIsSendPressed(true);
      setSuccess(true);
      setShowAgeInput(true);
    }
    setLoadingHistory(false);
  };

  const toggleCreditExpiredBottomSheet = (action: "open" | "close") => {
    switch (action) {
      case "open":
        creditExpiredRef.current?.show();
        break;
      case "close":
        creditExpiredRef.current?.hide();
        break;
      default:
        break;
    }
  };

  let headerButtons: HeaderButton[] = [
    {
      icon: <DDxHistory />,
      onClick: () => {
        trackZymedEvent(ZYMED_EVENTS.DDX_HISTORY_OPENED, {
          source: "in_chat_history_icon",
        });
        setIsHistoryDrawerOpen(true);
      },
    },
  ];

  if (!!age && !!gender) {
    headerButtons = [
      ...headerButtons,
      {
        icon: <DDxNewChat />,
        onClick: () => {
          if (!!historyId) window.location.replace(`/generate-ddx`);
          else if (success) onPressStartNewChat();
          else {
            trackZymedEvent(ZYMED_EVENTS.DDX_EXIT_CONFIRMATION_SHOWN, {
              case_id: lastCaseId.current,
              step_at_exit: !gender ? "gender" : !age ? "age" : "symptoms",
            });
            newChatConfirmationRef.current?.show();
          }
        },
      },
    ];
  }

  useEffect(() => {
    if (gender) setShowAgeInput(true);
    if (age) setShowSyptomInput(true);
  }, [age, gender, symptomText]);

  useEffect(() => {
    if (!!gender) setIsEditing((ie) => ({ ...ie, gender: false }));
    if (!!age) setIsEditing((ie) => ({ ...ie, age: false }));
  }, [gender, age, symptomText]);

  useEffect(() => {
    if (!!historyId) fetchHistoryById();
    canUserGenerateDDx();
  }, []);

  return (
    <>
      <Header
        title="Generate DDx"
        rightButtons={headerButtons}
        onPressBack={onPressBack}
        credits={credits}
      />
      {loadingHistory ? (
        <div
          className="relative w-full min-h-screen p-4  no-scrollbar bg-cover bg-center bg-no-repeat bg-fixed"
          style={{
            backgroundImage: `url(${BGImage})`,
          }}
        >
          <Spin size="large" />
        </div>
      ) : (
        <div
          className="relative w-full p-4  no-scrollbar bg-cover bg-center bg-no-repeat bg-fixed"
          style={{
            backgroundImage: `url(${BGImage})`,
            minHeight: `calc(100vh - ${showSyptomInput &&
              !isSendPressed &&
              !(isEditing.age || isEditing.gender)
              ? "260px"
              : "48px"
              })`,
          }}
        >
          <AnimatePresence>
            <motion.div
              key={"gender-div"}
              variants={itemVariants}
              initial="hidden"
              animate="show"
            >
              <AiMessageBubble
                boldText="Patient's Gender"
                text="Let's get started! To begin, please select the Patient's Gender from the options below"
                disabled={isEditing.age || isEditing.symptom}
              />
            </motion.div>

            <motion.div
              key={"gender-input-div"}
              variants={itemVariants}
              initial="hidden"
              animate="show"
            >
              <PatientGenderInput
                gender={gender}
                setGender={selectGender}
                disabled={isEditing.age || isEditing.symptom}
                onPressEdit={() => onEditPress("gender")}
                onPressSend={(gender?: "M" | "F" | null) =>
                  onPressSend(null, undefined, gender)
                }
              />
            </motion.div>

            {showAgeInput && (
              <motion.div
                key={"age-div"}
                variants={itemVariants}
                initial="hidden"
                animate="show"
              >
                <AiMessageBubble
                  boldText="Patient's Age"
                  text="Great! Please select the Patient's Age"
                  disabled={isEditing.gender || isEditing.symptom}
                />
              </motion.div>
            )}

            {showAgeInput && (
              <motion.div
                key={"age-input-div"}
                variants={itemVariants}
                initial="hidden"
                animate="show"
              >
                <PatientAgeInput
                  age={age}
                  setAge={selectAge}
                  disabled={isEditing.gender || isEditing.symptom}
                  onPressEdit={() => onEditPress("age")}
                  onPressSend={onPressSend}
                />
              </motion.div>
            )}

            {showSyptomInput && (
              <motion.div
                key={"symptom-div"}
                variants={itemVariants}
                initial="hidden"
                animate="show"
              >
                <AiMessageBubble
                  boldText="Symptoms"
                  text="Thank you! Now, please add the Symptoms along with any relevant details like the patient's history, past data, or clinical findings for better accuracy."
                  disabled={isEditing.age || isEditing.gender}
                />
                {isSendPressed
                  ? symptomText.length > 0 && (
                    <PatientTextInput
                      text={symptomText}
                      isDDxGenerated={!!generatedDDx}
                      disabled={isEditing.age || isEditing.gender}
                      onPressEdit={() => onEditPress("symptom")}
                    />
                  )
                  : (ddxHistoryCount ?? 0) <= 0 && (
                    <ExamplePrompts
                      onClickPrompt={onClickPrompt}
                      disabled={isEditing.age || isEditing.gender}
                    />
                  )}
              </motion.div>
            )}

            {sending ? (
              <motion.div
                key={"loading-text-div"}
                variants={itemVariants}
                initial="hidden"
                animate="show"
              >
                <AiMessageBubble text="Generating AI powered diagnosis ..." />
                <ShimmerSkeleton />
              </motion.div>
            ) : success ? (
              <motion.div
                key={"ddx-result-div"}
                variants={itemVariants}
                initial="hidden"
                animate="show"
              >
                <AiMessageBubble
                  text="Your DDx has been generated! Please find the results below."
                  disabled={
                    isEditing.age || isEditing.gender || isEditing.symptom
                  }
                />
                <DiagnosisView
                  caseId={caseId}
                  generatedDDx={generatedDDx}
                  disabled={
                    isEditing.age || isEditing.gender || isEditing.symptom
                  }
                />
              </motion.div>
            ) : sendError ? (
              <motion.div
                key={"ddx-error-div"}
                variants={itemVariants}
                initial="hidden"
                animate="show"
              >
                <AiMessageBubble text={sendError} />
              </motion.div>
            ) : null}
          </AnimatePresence>
        </div>
      )}
      {showSyptomInput &&
        !isSendPressed &&
        !(isEditing.age || isEditing.gender) && (
          <DDxInputField
            value={symptomText}
            setValue={setSymptomText}
            onPressSend={onPressSend}
            inputMode={inputMode}
            setInputMode={setInputMode}
            isEditMode={isEditing.symptom}
            cancelEditMode={cancelEditMode}
            toggleCreditExpiredBottomSheet={toggleCreditExpiredBottomSheet}
          />
        )}
      <BottomSheetWrapper ref={newChatConfirmationRef}>
        <div className="p-6 flex flex-col gap-4">
          <div className="flex flex-row items-start gap-3">
            <p className="text-[#1A1C1E] text-xl font-semibold text-left">
              Are you sure you want to start a new chat?
            </p>
            <button onClick={onPressCancelNewChat}>
              <Close className="min-h-6 min-w-6" />
            </button>
          </div>
          <p className="text-[#3D4854] font-normal text-base text-left">
            You haven&apos;t generated DDx in this chat yet. You can find it in
            the chat history to continue anytime in the future.
          </p>
          <div className="flex flex-row gap-4 items-center">
            <button
              onClick={onPressCancelNewChat}
              style={{ borderWidth: "1px" }}
              className="overflow-hidden gap-2 self-stretch px-4 py-4 mt-6 w-full text-lg font-medium leading-none text-center bg-white text-indigo-600 border-indigo-600 rounded-2xl min-h-[56px]"
            >
              No, I&apos;ll stay
            </button>
            <Button label="Start New Chat" onClick={onPressStartNewChat} />
          </div>
        </div>
      </BottomSheetWrapper>
      <BottomSheetWrapper ref={creditExpiredRef}>
        <div className="p-6 flex flex-col gap-4">
          <div className="flex flex-row items-start gap-3">
            <p className="text-[#1A1C1E] text-xl font-semibold text-left">
              You've Reached Your DDx Limit
            </p>
            <button onClick={() => toggleCreditExpiredBottomSheet("close")}>
              <Close className="min-h-6 min-w-6" />
            </button>
          </div>
          <p className="text-[#3D4854] font-normal text-base text-left">
            You have used all <span className="font-semibold">5 credits</span> for Differential
            Diagnosis (DDx) generation. To continue using this feature, please contact us for more access.
          </p>
          <div className="bg-white p-4 space-y-3 w-min border border-[#D0D5DD] rounded-2xl">
            <a
              href="tel:+919974042363"
              className="flex items-center text-[#4B4AD5] text-sm font-semibold"
            >
              <span>📞</span> <p className="underline ml-2">+91 99740 42363</p>
            </a>

            <a
              href="mailto:support@tatvacare.in"
              className="flex items-center text-[#4B4AD5] text-sm font-semibold"
            >
              <span>📩</span> <p className="underline ml-2">support@tatvacare.in</p>
            </a>
          </div>
        </div>
      </BottomSheetWrapper>
      <HistoryDrawer
        open={isHistoryDrawerOpen}
        onClose={() => setIsHistoryDrawerOpen(false)}
        onPressHistoryItem={onPressHistoryItem}
      />
    </>
  );
};

export default GenerateDDxPage;